import { SamdOrderRepository } from '@samd/api/order_repository';
import { FakeOrderRepository } from 'src/api/fake_order_repository';
import type { DownloadReportResult } from 'src/api/order_repository';
import {
  NccnRiskCategory,
  NStage,
  OrderStatus,
  SpecimenType,
  TStage,
  SamdOrderDetailFieldsFragment,
  UploadedFileCategory,
  UploadedFileStatus,
  UploadedFileFieldsFragment,
} from 'src/types';
import { DistributionPlatform } from 'src/types/env';

function makeUploadedFile(
  overrides: Pick<UploadedFileFieldsFragment, 'id' | 'filename'> &
    Partial<UploadedFileFieldsFragment>,
): UploadedFileFieldsFragment {
  return {
    category: UploadedFileCategory.SlideImage,
    status: UploadedFileStatus.Complete,
    progress: 100,
    createdAt: '2024-10-01T12:00:00Z',
    uploaderAuth0Id: null,
    ...overrides,
  };
}

function makeSamdOrder(
  overrides: Partial<SamdOrderDetailFieldsFragment> &
    Pick<SamdOrderDetailFieldsFragment, 'id' | 'status' | 'specimenType'>,
): SamdOrderDetailFieldsFragment {
  return {
    nStage: null,
    tumorSize: null,
    patientAge: null,
    reportRejectionReason: null,
    reportCustomRejectionReason: null,
    crossOrgId: overrides.id,
    createdAt: null,
    updatedAt: null,
    submittedAt: null,
    statusUpdatedAt: null,
    creatorAuth0Id: null,
    externalId: null,
    nccnRiskCategory: null,
    primaryGleasonScore: null,
    secondaryGleasonScore: null,
    psa: null,
    tStage: null,
    surgicalMargin: null,
    statusMessage: null,
    patient: null,
    uploadedFiles: { edges: [] },
    ...overrides,
  };
}

const demoProstateOrderWithPatient = makeSamdOrder({
  id: 'T3JkZXI6NQ==', // btoa('Order:5')
  externalId: 'demo_patient_id_1',
  crossOrgId: 'demo_cross_org_id_1',
  status: OrderStatus.Draft,
  specimenType: SpecimenType.ProstateBiopsy,
  primaryGleasonScore: 3,
  secondaryGleasonScore: 4,
  patientAge: 65,
  tStage: TStage.T2A,
  patient: {
    firstNamePhi: 'John',
    lastNamePhi: 'Demo',
    dateOfBirthPhi: '1960-05-15',
  },
  createdAt: '2023-10-01T12:00:00Z',
  updatedAt: '2023-10-01T12:00:00Z',
  statusUpdatedAt: '2023-10-01T12:00:00Z',
});

const prostateOrder1 = makeSamdOrder({
  id: 'T3JkZXI6MQ==', // btoa('Order:1')
  externalId: 'p_patient_id_1',
  crossOrgId: 'cross_org_id_1',
  status: OrderStatus.Draft,
  specimenType: SpecimenType.ProstateBiopsy,
  primaryGleasonScore: 3,
  secondaryGleasonScore: 4,
  psa: 1.0,
  patientAge: 65,
  tStage: TStage.T1C,
  createdAt: '2023-10-01T12:00:00Z',
  updatedAt: '2023-10-01T12:00:00Z',
  statusUpdatedAt: '2023-10-01T12:00:00Z',
});

const prostateOrder2 = makeSamdOrder({
  id: 'T3JkZXI6Mg==', // btoa('Order:2')
  externalId: 'p_patient_id_2',
  crossOrgId: 'cross_org_id_2',
  status: OrderStatus.LabProcessing,
  specimenType: SpecimenType.BreastBiopsy,
  primaryGleasonScore: 3,
  secondaryGleasonScore: 3,
  psa: 2.8,
  patientAge: 72,
  tStage: TStage.T1A,
  nccnRiskCategory: NccnRiskCategory.Low,
  createdAt: '2023-10-01T12:00:00Z',
  updatedAt: '2023-10-01T12:00:00Z',
  submittedAt: '2023-10-01T12:00:00Z',
  statusUpdatedAt: '2023-10-01T12:00:00Z',
});

const prostateOrder3 = makeSamdOrder({
  id: 'T3JkZXI6Mw==', // btoa('Order:3')
  externalId: 'p_patient_id_3',
  crossOrgId: 'AZ-4T-LD7-89JR',
  status: OrderStatus.ReportReady,
  specimenType: SpecimenType.ProstateBiopsy,
  primaryGleasonScore: 4,
  secondaryGleasonScore: 3,
  psa: 2.8,
  patientAge: 72,
  tStage: TStage.T1A,
  nccnRiskCategory: NccnRiskCategory.IntermediateFavorable,
  createdAt: '2024-10-01T12:00:00Z',
  updatedAt: '2024-10-01T12:00:00Z',
  submittedAt: '2024-10-01T12:00:00Z',
  statusUpdatedAt: '2024-10-01T12:00:00Z',
  uploadedFiles: {
    edges: [
      {
        node: makeUploadedFile({
          id: '1',
          filename: 'slide_image_1.svs',
        }),
      },
    ],
  },
});

const prostateOrder4 = makeSamdOrder({
  id: 'T3JkZXI6NA==', // btoa('Order:4')
  externalId: 'p_patient_id_4',
  crossOrgId: 'AZ-4T-LD7-89JQ',
  status: OrderStatus.AiError,
  statusMessage: 'AI Error: Image Blurry, Please Upload New Image',
  specimenType: SpecimenType.ProstateBiopsy,
  primaryGleasonScore: 3,
  secondaryGleasonScore: 3,
  psa: 2.8,
  patientAge: 72,
  tStage: TStage.T1A,
  nccnRiskCategory: NccnRiskCategory.Low,
  createdAt: '2024-11-01T12:00:00Z',
  updatedAt: '2024-11-01T12:00:00Z',
  submittedAt: '2024-11-01T12:00:00Z',
  statusUpdatedAt: '2024-11-01T12:00:00Z',
  uploadedFiles: {
    edges: [
      {
        node: makeUploadedFile({
          id: '2',
          filename: 'slide_image_2.svs',
          createdAt: '2024-11-01T12:00:00Z',
        }),
      },
    ],
  },
});

/** Report Ready breast order eligible for CDS + combined downloads (age >= 50, N0). */
const breastReportReadyOrder = makeSamdOrder({
  patientAge: 58,
  tumorSize: 2.1,
  nStage: NStage.N0,
  id: 'T3JkZXI6Ng==', // btoa('Order:6')
  externalId: 'b_patient_id_1',
  crossOrgId: 'AZ-BR-CDS-001',
  status: OrderStatus.ReportReady,
  specimenType: SpecimenType.BreastBiopsy,
  createdAt: '2024-12-01T12:00:00Z',
  updatedAt: '2024-12-01T12:00:00Z',
  submittedAt: '2024-12-01T12:00:00Z',
  statusUpdatedAt: '2024-12-01T12:00:00Z',
  uploadedFiles: {
    edges: [
      {
        node: makeUploadedFile({
          id: '3',
          filename: 'breast_slide_1.svs',
          createdAt: '2024-12-01T12:00:00Z',
        }),
      },
    ],
  },
});

/**
 * Implementation of the Order Repository for the Samd platform on a fake backend.
 */
export class SamdFakeOrderRepository
  extends FakeOrderRepository<SamdOrderDetailFieldsFragment>
  implements SamdOrderRepository
{
  readonly _platform = DistributionPlatform.Samd;
  readonly _storageKey = 'samdProstateOrders';
  readonly _initialOrders = [
    prostateOrder1,
    prostateOrder2,
    prostateOrder3,
    prostateOrder4,
    breastReportReadyOrder,
    demoProstateOrderWithPatient,
  ];

  async downloadSlideImage(_fileId: string): Promise<string> {
    return Promise.resolve('');
  }

  async downloadClinicalDecisionSupport(
    _orderId: string,
  ): Promise<DownloadReportResult> {
    return Promise.resolve({ success: true, url: '' });
  }

  async downloadCombinedReport(
    _orderId: string,
  ): Promise<DownloadReportResult> {
    return Promise.resolve({ success: true, url: '' });
  }

  submit(
    order: SamdOrderDetailFieldsFragment,
  ): Promise<SamdOrderDetailFieldsFragment> {
    return this.update({
      ...order,
      status: OrderStatus.LabProcessing,
    });
  }

  resubmit(
    order: SamdOrderDetailFieldsFragment,
  ): Promise<SamdOrderDetailFieldsFragment> {
    return this.update({
      ...order,
      status: OrderStatus.LabProcessing,
      submittedAt: new Date().toISOString(),
      statusUpdatedAt: new Date().toISOString(),
    });
  }
}
